'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useState, useCallback, useMemo, Suspense } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useInstantSearch } from 'react-instantsearch';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import ProductListingPageName from 'components/consumer/ProductListingPage/components/ProductListingPageName';
import DesktopShowHideFiltersButton from 'components/consumer/ProductListingPage/components/desktop/DesktopShowHideFiltersButton';
import MobileGridColumnToggle from 'components/consumer/ProductListingPage/components/mobile/MobileGridColumnToggle';
import DesktopProductListingSidebarContainer from 'components/consumer/ProductListingPage/components/desktop/DesktopProductListingSidebarContainer';
import useAdvancedSearchContext from 'components/consumer/consumer-search/advanced-search-next/hooks/useAdvancedSearchContext';
import AdvancedSearchProductHits from 'components/consumer/consumer-search/advanced-search-next/components/AdvancedSearchProductHits';
import DesktopProductListingColorFilter from 'components/consumer/consumer-search/advanced-search-next/components/AlgoliaHits/components/DesktopProductListingColorFilter';
import ProductListingSortBy from 'components/consumer/consumer-search/advanced-search-next/components/AlgoliaHits/components/ProductListingSortBy';
import ProductListingPageWidgets from 'components/consumer/ProductListingPageWidgets';
import ProductListingPageDescriptionShell from 'components/consumer/consumer-search/advanced-search-next/components/ProductListingPageDescriptionUI';
import useWarehouseIdForUser from 'global-state/warehouseIdForUser/useWarehouseIdForUser';
import CategoryAlgoliaMobileFilters from './CategoryAlgoliaMobileFilters';
import useCategoryAlgoliaAnalytics from '../hooks/useCategoryAlgoliaAnalytics';
import CategoryAlgoliaSidebar from './CategoryAlgoliaSidebar';
import CategoryCollectionBackLink from './CategoryCollectionBackLink';
import CategoryAlgoliaSEO from './CategoryAlgoliaSEO';
import CategoryAlgoliaJSONLD from './CategoryAlgoliaJSONLD';
import { isCollectionPageSlug } from '../categoryPageSlugUtils';
import { PAGE_TYPE, getCategoryPageTypeConfig } from '../categoryPageTypes';
const CategoryAlgoliaLayout = t0 => {
  const $ = _c(105);
  const {
    slug,
    pageType: t1,
    serverCategoryData,
    initialProducts,
    selectedFacets,
    onToggleFacet,
    onClearFacets
  } = t0;
  const pageType = t1 === undefined ? PAGE_TYPE.CATEGORY : t1;
  let t2;
  if ($[0] !== pageType) {
    t2 = getCategoryPageTypeConfig(pageType);
    $[0] = pageType;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const pageConfig = t2;
  const {
    currentProductIndex
  } = useAdvancedSearchContext();
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      skipSuspense: true
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    results,
    status
  } = useInstantSearch(t3);
  const warehouseId = useWarehouseIdForUser();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {};
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const [enrichedProductsByIndex, setEnrichedProductsByIndex] = useState(t4);
  let t5;
  if ($[4] !== pageConfig.expandsVariants || $[5] !== pageConfig.keepUnmatchedProducts || $[6] !== pageConfig.variantWarehouseField || $[7] !== warehouseId) {
    t5 = pageConfig.expandsVariants && pageConfig.variantWarehouseField ? {
      variantWarehouseField: pageConfig.variantWarehouseField,
      warehouseId,
      keepUnmatchedProducts: pageConfig.keepUnmatchedProducts
    } : null;
    $[4] = pageConfig.expandsVariants;
    $[5] = pageConfig.keepUnmatchedProducts;
    $[6] = pageConfig.variantWarehouseField;
    $[7] = warehouseId;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const variantMatching = t5;
  const enrichedProductsKey = variantMatching ? `${currentProductIndex}|${variantMatching.warehouseId}` : currentProductIndex;
  let t6;
  if ($[9] !== enrichedProductsByIndex || $[10] !== enrichedProductsKey) {
    t6 = enrichedProductsByIndex[enrichedProductsKey] ?? [];
    $[9] = enrichedProductsByIndex;
    $[10] = enrichedProductsKey;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const enrichedProducts = t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setIsSidebarOpen(_temp);
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const toggleSidebar = t7;
  let t8;
  if ($[13] !== enrichedProductsKey) {
    t8 = products => {
      setEnrichedProductsByIndex(prev_0 => ({
        ...prev_0,
        [enrichedProductsKey]: products
      }));
    };
    $[13] = enrichedProductsKey;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const handleProductsChange = t8;
  const categoryData = serverCategoryData?.getCategoryPage;
  const pageName = categoryData?.pageName || slug;
  const t9 = categoryData?.pageId;
  let t10;
  if ($[15] !== enrichedProducts || $[16] !== pageName || $[17] !== pageType || $[18] !== t9) {
    t10 = {
      pageId: t9,
      pageName,
      products: enrichedProducts,
      pageType
    };
    $[15] = enrichedProducts;
    $[16] = pageName;
    $[17] = pageType;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  useCategoryAlgoliaAnalytics(t10);
  const productsCount = results?.nbHits || 0;
  const hasProductsLoaded = status === "idle" || status === "error";
  const resultsCount = hasProductsLoaded && productsCount > 0 ? productsCount : null;
  const resultsCountText = resultsCount != null ? `${resultsCount} Products` : null;
  const isDefaultSortIndex = currentProductIndex === pageConfig.defaultSortIndex;
  const canSeedServerProducts = isDefaultSortIndex && enrichedProducts.length === 0;
  let t11;
  if ($[20] !== categoryData) {
    t11 = <CategoryAlgoliaSEO categoryData={categoryData} />;
    $[20] = categoryData;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const t12 = categoryData?.pageLongName || pageName;
  const t13 = categoryData?.pageLongDescription || "";
  const t14 = categoryData?.pageId;
  const t15 = results?.nbHits;
  let t16;
  if ($[22] !== currentProductIndex || $[23] !== enrichedProducts || $[24] !== hasProductsLoaded || $[25] !== t12 || $[26] !== t13 || $[27] !== t14 || $[28] !== t15) {
    t16 = <CategoryAlgoliaJSONLD products={enrichedProducts} pageName={t12} pageDescription={t13} pageId={t14} totalHits={t15} currentProductIndex={currentProductIndex} hasProductsLoaded={hasProductsLoaded} />;
    $[22] = currentProductIndex;
    $[23] = enrichedProducts;
    $[24] = hasProductsLoaded;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = classNames("px-3 py-4 space-y-1", "md:px-0 md:py-0 md:space-y-0", "md:items-center md:bg-white md:flex md:h-[53px] md:sticky md:z-[11] md:gap-5 md:justify-between", "md:top-[117px]");
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== pageName) {
    t18 = <ProductListingPageName pageName={pageName} showPageNameWhileLoading={true} />;
    $[31] = pageName;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== resultsCountText) {
    t19 = resultsCountText && <span className="text-sm text-gray-light2 font-normal">{resultsCountText}</span>;
    $[33] = resultsCountText;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <DesktopShowHideFiltersButton onClick={toggleSidebar} />;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== t18 || $[37] !== t19) {
    t21 = <div className="md:flex md:gap-5 md:items-center">{t18}{t19}{t20}</div>;
    $[36] = t18;
    $[37] = t19;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  let t23;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = classNames("flex flex-row justify-between gap-3 py-3", "md:block md:gap-0 md:py-0");
    t23 = classNames("flex-1 flex gap-1", "md:flex-initial md:block md:gap-0");
    $[39] = t22;
    $[40] = t23;
  } else {
    t22 = $[39];
    t23 = $[40];
  }
  const t24 = categoryData?.filters;
  let t25;
  if ($[41] !== onToggleFacet || $[42] !== pageType || $[43] !== productsCount || $[44] !== selectedFacets || $[45] !== t24) {
    t25 = <CategoryAlgoliaMobileFilters filters={t24} pageType={pageType} selectedFacets={selectedFacets} onToggleFacet={onToggleFacet} productsCount={productsCount} />;
    $[41] = onToggleFacet;
    $[42] = pageType;
    $[43] = productsCount;
    $[44] = selectedFacets;
    $[45] = t24;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== pageConfig.showColorFilter) {
    t26 = pageConfig.showColorFilter && <DesktopProductListingColorFilter />;
    $[47] = pageConfig.showColorFilter;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== pageConfig.defaultSortIndex || $[50] !== pageConfig.sortOptions) {
    t27 = <ProductListingSortBy sortByOptions={pageConfig.sortOptions} defaultProductIndex={pageConfig.defaultSortIndex} />;
    $[49] = pageConfig.defaultSortIndex;
    $[50] = pageConfig.sortOptions;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] !== t26 || $[53] !== t27) {
    t28 = <div className="flex-1 md:flex-initial md:flex md:flex-row md:gap-4 md:items-center">{t26}{t27}</div>;
    $[52] = t26;
    $[53] = t27;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== t25 || $[56] !== t28) {
    t29 = <div className={t23}>{t25}{t28}</div>;
    $[55] = t25;
    $[56] = t28;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  let t30;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <MobileGridColumnToggle />;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  let t31;
  if ($[59] !== t29) {
    t31 = <div className={t22}>{t29}{t30}</div>;
    $[59] = t29;
    $[60] = t31;
  } else {
    t31 = $[60];
  }
  let t32;
  if ($[61] !== t21 || $[62] !== t31) {
    t32 = <div className={t17}>{t21}{t31}</div>;
    $[61] = t21;
    $[62] = t31;
    $[63] = t32;
  } else {
    t32 = $[63];
  }
  let t33;
  if ($[64] !== pageConfig.showCollectionBackLink || $[65] !== slug) {
    t33 = (pageConfig.showCollectionBackLink || isCollectionPageSlug(slug)) && <CategoryCollectionBackLink />;
    $[64] = pageConfig.showCollectionBackLink;
    $[65] = slug;
    $[66] = t33;
  } else {
    t33 = $[66];
  }
  const t34 = categoryData?.filters;
  let t35;
  if ($[67] !== onClearFacets || $[68] !== onToggleFacet || $[69] !== pageName || $[70] !== pageType || $[71] !== selectedFacets || $[72] !== t34) {
    t35 = <CategoryAlgoliaSidebar pageName={pageName} pageType={pageType} filters={t34} selectedFacets={selectedFacets} onToggleFacet={onToggleFacet} onClearFacets={onClearFacets} />;
    $[67] = onClearFacets;
    $[68] = onToggleFacet;
    $[69] = pageName;
    $[70] = pageType;
    $[71] = selectedFacets;
    $[72] = t34;
    $[73] = t35;
  } else {
    t35 = $[73];
  }
  let t36;
  if ($[74] !== isSidebarOpen || $[75] !== t33 || $[76] !== t35) {
    t36 = <div className="hidden md:block"><DesktopProductListingSidebarContainer isOpen={isSidebarOpen}>{t33}{t35}</DesktopProductListingSidebarContainer></div>;
    $[74] = isSidebarOpen;
    $[75] = t33;
    $[76] = t35;
    $[77] = t36;
  } else {
    t36 = $[77];
  }
  const t37 = canSeedServerProducts ? initialProducts : undefined;
  const t38 = canSeedServerProducts ? serverCategoryData?.getCategoryPage : undefined;
  let t39;
  if ($[78] !== currentProductIndex || $[79] !== handleProductsChange || $[80] !== isDefaultSortIndex || $[81] !== t37 || $[82] !== t38 || $[83] !== variantMatching) {
    t39 = <AdvancedSearchProductHits key={currentProductIndex} onProductsChange={handleProductsChange} initialProducts={t37} seedServerData={t38} eagerFirstPageImages={isDefaultSortIndex} enableShowAllButton={true} variantMatching={variantMatching} />;
    $[78] = currentProductIndex;
    $[79] = handleProductsChange;
    $[80] = isDefaultSortIndex;
    $[81] = t37;
    $[82] = t38;
    $[83] = variantMatching;
    $[84] = t39;
  } else {
    t39 = $[84];
  }
  const t40 = categoryData?.pageLongDescription || "";
  let t41;
  if ($[85] !== pageName || $[86] !== t40) {
    t41 = <ProductListingPageDescriptionShell pageLongDescription={t40} pageName={pageName} />;
    $[85] = pageName;
    $[86] = t40;
    $[87] = t41;
  } else {
    t41 = $[87];
  }
  let t42;
  if ($[88] !== t39 || $[89] !== t41) {
    t42 = <div className="md:flex md:flex-col md:gap-8 md:mb-8 md:flex-1">{t39}{t41}</div>;
    $[88] = t39;
    $[89] = t41;
    $[90] = t42;
  } else {
    t42 = $[90];
  }
  let t43;
  if ($[91] !== t36 || $[92] !== t42) {
    t43 = <div className="md:flex md:flex-row md:gap md:mt-2">{t36}{t42}</div>;
    $[91] = t36;
    $[92] = t42;
    $[93] = t43;
  } else {
    t43 = $[93];
  }
  let t44;
  if ($[94] !== categoryData?.pageId || $[95] !== pageConfig.showBestSellersCarousel) {
    t44 = pageConfig.showBestSellersCarousel && <Suspense fallback={null}><ProductListingPageWidgets categoryId={categoryData?.pageId} /></Suspense>;
    $[94] = categoryData?.pageId;
    $[95] = pageConfig.showBestSellersCarousel;
    $[96] = t44;
  } else {
    t44 = $[96];
  }
  let t45;
  if ($[97] !== t32 || $[98] !== t43 || $[99] !== t44) {
    t45 = <FixedWidthContainer wide={true} noMobilePadding={true}>{t32}{t43}{t44}</FixedWidthContainer>;
    $[97] = t32;
    $[98] = t43;
    $[99] = t44;
    $[100] = t45;
  } else {
    t45 = $[100];
  }
  let t46;
  if ($[101] !== t11 || $[102] !== t16 || $[103] !== t45) {
    t46 = <>{t11}{t16}{t45}</>;
    $[101] = t11;
    $[102] = t16;
    $[103] = t45;
    $[104] = t46;
  } else {
    t46 = $[104];
  }
  return t46;
};
CategoryAlgoliaLayout.propTypes = {
  slug: PropTypes.string.isRequired,
  pageType: PropTypes.oneOf(Object.values(PAGE_TYPE)),
  serverCategoryData: PropTypes.object,
  initialProducts: PropTypes.array,
  selectedFacets: PropTypes.object,
  onToggleFacet: PropTypes.func,
  onClearFacets: PropTypes.func
};
export default CategoryAlgoliaLayout;
function _temp(prev) {
  return !prev;
}